import axios from 'axios'

export interface FileDownloadProgress {
  [id: string]: number
}

export interface FileDownloadAnalytics {
  size: number
  timeToFetch: number
  cacheHit: boolean
}

export interface FileDownloadResult {
  files: Array<{ id: string; file: Blob }>
  progress: FileDownloadProgress
  analytics: Map<string, FileDownloadAnalytics>
}

type FileToFetch = {
  id: string
  url: string
}

type Props = {
  filesToFetch: FileToFetch[]
  onProgress: (progress: FileDownloadProgress) => void
}

export const fetchFiles = async ({
  filesToFetch,
  onProgress,
}: Props): Promise<FileDownloadResult> => {
  const progressMap: FileDownloadProgress = {}
  const analyticsMap: Map<string, FileDownloadAnalytics> = new Map()

  const validFilesToFetch = filesToFetch.filter(
    file => typeof file.url === 'string' && file.url.trim() !== '',
  )

  const requests = validFilesToFetch.map(async file => {
    const startTime = performance.now()
    const response = await axios.get(file.url, {
      responseType: 'blob',
      onDownloadProgress: event => {
        const totalLength = Number(
          event.event.target.getResponseHeader('Content-Length'),
        )
        if (totalLength) {
          analyticsMap.set(file.id, {
            size: totalLength,
            timeToFetch: -1,
            cacheHit: false,
          })
          progressMap[file.id] = Math.round((event.loaded * 100) / totalLength)
          onProgress({ ...progressMap })
        }
      },
    })

    const endTime = performance.now()
    const timeToFetch = endTime - startTime

    const analytics = analyticsMap.get(file.id)
    analyticsMap.set(file.id, {
      size: analytics?.size ?? -1,
      timeToFetch,
      cacheHit: timeToFetch < 50,
    })

    return response
  })

  const responses = await Promise.all(requests)
  const files: Array<{ id: string; file: Blob }> = []

  responses.forEach((response, index) => {
    files.push({ id: filesToFetch[index].id, file: response.data })
  })

  return { files, progress: progressMap, analytics: analyticsMap }
}
